import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Virtuoso } from 'react-virtuoso';
import GlobalOverviewSlot from './GlobalOverviewSlot';
import DateUtils from '../../utils/DateUtils';
import { ReactComponent as Orientation } from '../../assets/images/compass-grayscale-600.svg';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../../constants';
import ShiftTypeBadge from '../shifts/ShiftTypeBadge';

const GlobalOverviewDay = ({
	dayShifts,
	day,
	periodIndex,
	setShowRefreshModal,
	handleSlotAction,
	showOptions,
	globalScheduleType,
	shiftsScroller,
	matchingShifts,
	unitsByFacility,
	editFlexShiftFacilitiesByManagementCompany
}) => {
	const { showFilled, showOpen, showConfirmed, showCancelled } = showOptions;
	const TODAY = useMemo(() => moment().format('ddd M/D'), []);
	const formattedDay = useMemo(() => DateUtils.getFormattedDateByOffset(periodIndex, day), [periodIndex, day]);

	const shiftStatusClass = (shift) => {
		if (shift.unfilledSlots.length) {
			return 'unfilled';
		}
		if (showCancelled && !shift.filledSlots.length) {
			return 'cancelled';
		}
		return '';
	};
	const parsedShifts = Object.values(dayShifts?.shifts ?? {});

	return (
		<div className="col">
			<div className="summary-box">
				<div className={classNames('day-label', { 'today-label': !periodIndex && formattedDay === TODAY })}>
					{formattedDay}
				</div>
				<div className="summary-container">
					<div className="filled-count">Filled: {dayShifts?.filledSlotsCount}</div>
					<div className="unfilled-count">Open: {dayShifts?.unfilledSlotsCount}</div>
				</div>
			</div>
			{matchingShifts?.haveShifts && (
				<div className="shifts-container">
					{!!parsedShifts.length && (
						<Virtuoso
							customScrollParent={shiftsScroller}
							data={parsedShifts}
							itemContent={(_index, shift) => (
								<div
									className={classNames('shift-container', shiftStatusClass(shift))}
									key={`shift-${shift.identifier}`}
								>
									<div className="shift-box">
										{globalScheduleType === GLOBAL_CALENDAR_SHIFT_TYPES.ALL && (
											<div className="shift-box__type-pills">
												{Array.from(shift.shift_types).map((shiftType) => (
													<ShiftTypeBadge shiftType={shiftType} key={`shift-pill-${shiftType}-${shift.identifier}`} />
												))}
											</div>
										)}

										<div className="facility-name">{shift.facility_name}</div>
										<div>
											{shift.shift_resource_type}
											{shift.shift_specialty && ` - ${shift.shift_specialty}`}
										</div>
										{!!shift.is_orientation && (
											<div className="orientation-pill">
												<Orientation />
												Orientation
											</div>
										)}
										<div>
											{shift.formatted_start_time} - {shift.formatted_end_time}
										</div>
										<div className="fill-summary">
											{shift?.filledSlotsCount} of {shift?.filledSlotsCount + shift?.unfilledSlotsCount} Filled
										</div>
									</div>
									{(showFilled || showConfirmed) &&
										shift.filledSlots.map(({ shift: filledShift, slot }) => (
											<GlobalOverviewSlot
												key={`filled-slot-${filledShift.shift_type}-${slot.slot_id}`}
												slot={slot}
												shift={filledShift}
												showRefreshModal={setShowRefreshModal}
												handleSlotAction={handleSlotAction}
												shiftsForAllDay={dayShifts?.shifts || {}}
												isLastDay={day === 6}
												isUpcomingShift={shift.isUpcomingShift}
												globalScheduleType={globalScheduleType}
												hasAvailableUnits={unitsByFacility[shift.facility_id]?.length > 0}
												hasUnitsEnabled={shift.hasUnitsEnabled}
												hasEnoughFlexFacilitiesToFloat={
													editFlexShiftFacilitiesByManagementCompany[slot.management_company_id]?.length > 1
												}
											/>
										))}
									{(showOpen || showConfirmed) &&
										shift.unfilledSlots.map(({ shift: unfilledShift, slot }) => (
											<GlobalOverviewSlot
												key={`unfilled-slot-${unfilledShift.shift_type}-${slot.slot_id}`}
												slot={slot}
												shift={unfilledShift}
												customClass="unfilled"
												showRefreshModal={setShowRefreshModal}
												handleSlotAction={handleSlotAction}
												shiftsForAllDay={dayShifts?.shifts || {}}
												isLastDay={day === 6}
												isUpcomingShift={shift.isUpcomingShift}
												globalScheduleType={globalScheduleType}
												hasAvailableUnits={unitsByFacility[shift.facility_id]?.length > 0}
												hasUnitsEnabled={shift.hasUnitsEnabled}
												hasEnoughFlexFacilitiesToFloat={
													editFlexShiftFacilitiesByManagementCompany[slot.management_company_id]?.length > 1
												}
											/>
										))}
									{showCancelled &&
										shift.cancelledSlots.map(({ shift: cancelledShift, slot }) => (
											<GlobalOverviewSlot
												key={`cancelled-slot-${cancelledShift.shift_type}-${slot.slot_id}`}
												slot={slot}
												shift={cancelledShift}
												customClass="cancelled"
												showRefreshModal={setShowRefreshModal}
												handleSlotAction={handleSlotAction}
												shiftsForAllDay={dayShifts?.shifts || {}}
												isLastDay={day === 6}
												isUpcomingShift={shift.isUpcomingShift}
												globalScheduleType={globalScheduleType}
												hasAvailableUnits={unitsByFacility[shift.facility_id]?.length > 0}
												hasUnitsEnabled={shift.hasUnitsEnabled}
												hasEnoughFlexFacilitiesToFloat={
													editFlexShiftFacilitiesByManagementCompany[slot.management_company_id]?.length > 1
												}
											/>
										))}
								</div>
							)}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default memo(GlobalOverviewDay);
